class AtlasCollectionLoadMore extends HTMLElement {
    constructor() {
        super();
        this.currentPage = 1;
        this.totalPages = 1;
        this.isLoading = false;
        this.isInitialized = false;
        this.init();
    }

    init() {
        const loadMoreButton = this.querySelector(".load-more-button");
        if (!loadMoreButton) return;

        this.currentPage = parseInt(
            loadMoreButton.dataset.currentPage || "1",
            10
        );
        this.totalPages = parseInt(
            loadMoreButton.dataset.totalPages || "1",
            10
        );
        const productsPerPage = parseInt(
            loadMoreButton.dataset.productsPerPage || "4",
            10
        );

        const productGrid = document.getElementById("product-grid");
        if (!productGrid || !productGrid.dataset.id) {
            console.error(
                "AtlasCollectionLoadMore: product-grid element with data-id is required"
            );
            return;
        }

        this.sectionId = productGrid.dataset.id;
        this.productsPerPage = productsPerPage;

        loadMoreButton.addEventListener("click", (e) => {
            e.preventDefault();
            this.loadNextPage();
        });

        this.updateButtonVisibility();

        this.loadPagesFromURL();
        this.isInitialized = true;
    }

    async loadPagesFromURL() {
        const urlParams = new URLSearchParams(window.location.search);
        const urlPage = parseInt(urlParams.get("page") || "1", 10);

        if (urlPage > 1 && urlPage <= this.totalPages) {
            const pagesToLoad = [];
            for (let page = urlPage - 1; page >= 1; page--) {
                pagesToLoad.push(page);
            }

            for (const page of pagesToLoad) {
                const success = await this.loadPage(page, false, true, true); // prepend=true, skip pagination update
                if (!success) {
                    return;
                }
            }

            await this.updatePaginationFromPage(urlPage);

            const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
            window.history.replaceState(
                { searchParams: urlParams.toString() },
                "",
                newUrl
            );
        } else if (urlPage > this.totalPages) {
            urlParams.delete("page");
            const newUrl = `${window.location.pathname}${urlParams.toString() ? "?" + urlParams.toString() : ""}`;
            window.history.replaceState(
                { searchParams: urlParams.toString() },
                "",
                newUrl
            );
        } else if (urlPage === 1 && urlParams.has("page")) {
            urlParams.delete("page");
            const newUrl = `${window.location.pathname}${urlParams.toString() ? "?" + urlParams.toString() : ""}`;
            window.history.replaceState(
                { searchParams: urlParams.toString() },
                "",
                newUrl
            );
        }
    }

    async loadPage(
        targetPage,
        updateURL = true,
        forceLoad = false,
        prepend = false
    ) {
        if (
            this.isLoading ||
            targetPage > this.totalPages ||
            (!forceLoad && targetPage <= this.currentPage)
        ) {
            return false;
        }

        this.isLoading = true;
        const loadMoreButton = this.querySelector(".load-more-button");
        const paginationText = this.querySelector(".pagination-text");
        const productGrid = document.getElementById("product-grid");

        if (!loadMoreButton || !productGrid) {
            this.isLoading = false;
            return false;
        }

        try {
            const currentParams = new URLSearchParams(window.location.search);

            currentParams.set("page", targetPage.toString());

            const url = `${window.location.pathname}?section_id=${this.sectionId}&${currentParams.toString()}`;

            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");

            const newProductGrid = doc.getElementById("product-grid");
            if (!newProductGrid) {
                throw new Error("Product grid not found in response");
            }

            if (!prepend) {
                this.updateProductGridAttributes(productGrid, newProductGrid);
            }

            const newProducts = Array.from(newProductGrid.children);

            const currentPagination = productGrid.querySelector(
                ".atlas-collection-pagination"
            );

            if (prepend) {
                const insertBefore =
                    currentPagination || productGrid.firstChild;

                newProducts.forEach((product) => {
                    productGrid.insertBefore(product, insertBefore);
                });
            } else {
                newProducts.forEach((product) => {
                    productGrid.appendChild(product);
                });
            }

            if (!prepend) {
                const newPagination = doc.querySelector(
                    ".atlas-collection-pagination"
                );
                if (newPagination) {
                    const newPaginationText =
                        newPagination.querySelector(".pagination-text");
                    const newLoadMoreButton =
                        newPagination.querySelector(".load-more-button");

                    if (newPaginationText && paginationText) {
                        paginationText.innerHTML = newPaginationText.innerHTML;
                    }

                    if (newLoadMoreButton) {
                        this.currentPage = parseInt(
                            newLoadMoreButton.dataset.currentPage ||
                                targetPage.toString(),
                            10
                        );
                        this.totalPages = parseInt(
                            newLoadMoreButton.dataset.totalPages ||
                                this.totalPages.toString(),
                            10
                        );

                        loadMoreButton.dataset.currentPage =
                            this.currentPage.toString();
                        loadMoreButton.dataset.totalPages =
                            this.totalPages.toString();
                    }
                }
            } else {
                this.currentPage = targetPage;
            }

            if (updateURL) {
                const newUrl = `${window.location.pathname}?${currentParams.toString()}`;
                window.history.replaceState(
                    { searchParams: currentParams.toString() },
                    "",
                    newUrl
                );
            }

            if (
                updateURL &&
                typeof initializeScrollAnimationTrigger === "function"
            ) {
                initializeScrollAnimationTrigger(doc.body.innerHTML);
            }

            this.updateButtonVisibility();

            return true;
        } catch (error) {
            console.error(`Error loading page ${targetPage}:`, error);
            return false;
        } finally {
            this.isLoading = false;
        }
    }

    updateButtonVisibility() {
        const loadMoreButton = this.querySelector(".load-more-button");
        const paginationWrapper = this.querySelector(".pagination-wrapper");

        if (!loadMoreButton || !paginationWrapper) return;

        if (this.currentPage >= this.totalPages) {
            loadMoreButton.style.display = "none";
        } else {
            loadMoreButton.style.display = "";
        }
    }

    async loadNextPage() {
        if (this.isLoading || this.currentPage >= this.totalPages) return;

        const loadMoreButton = this.querySelector(".load-more-button");
        if (!loadMoreButton) return;

        loadMoreButton.disabled = true;
        const originalButtonText = loadMoreButton.textContent;
        loadMoreButton.textContent = "Loading...";

        try {
            const nextPage = this.currentPage + 1;
            const success = await this.loadPage(nextPage, true);

            if (!success) {
                throw new Error("Failed to load page");
            }

            if (this.currentPage < this.totalPages) {
                loadMoreButton.textContent = originalButtonText;
            }
        } catch (error) {
            console.error("Error loading more products:", error);
            loadMoreButton.textContent = "Error loading products";
            setTimeout(() => {
                loadMoreButton.textContent = originalButtonText;
            }, 3000);
        } finally {
            loadMoreButton.disabled = false;
        }
    }

    updateProductGridAttributes(currentGrid, newGrid) {
        if (newGrid.hasAttribute("hide-load-more")) {
            currentGrid.setAttribute("hide-load-more", "");
        } else {
            currentGrid.removeAttribute("hide-load-more");
        }

        Array.from(newGrid.attributes).forEach((attr) => {
            if (attr.name.startsWith("data-")) {
                currentGrid.setAttribute(attr.name, attr.value);
            }
        });

        const importantAttributes = ["class", "id"];
        importantAttributes.forEach((attrName) => {
            if (newGrid.hasAttribute(attrName)) {
                if (attrName === "class") {
                    const newClasses = newGrid
                        .getAttribute(attrName)
                        .split(" ");
                    const currentClasses =
                        currentGrid.getAttribute(attrName)?.split(" ") || [];
                    const mergedClasses = [
                        ...new Set([...currentClasses, ...newClasses])
                    ];
                    currentGrid.setAttribute(attrName, mergedClasses.join(" "));
                }
            }
        });
    }

    async updatePaginationFromPage(targetPage) {
        const paginationText = this.querySelector(".pagination-text");
        const loadMoreButton = this.querySelector(".load-more-button");
        if (!paginationText || !loadMoreButton) return;

        try {
            const currentParams = new URLSearchParams(window.location.search);
            currentParams.set("page", targetPage.toString());

            const url = `${window.location.pathname}?section_id=${this.sectionId}&${currentParams.toString()}`;

            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");

            const newPagination = doc.querySelector(
                ".atlas-collection-pagination"
            );
            if (newPagination) {
                const newPaginationText =
                    newPagination.querySelector(".pagination-text");
                const newLoadMoreButton =
                    newPagination.querySelector(".load-more-button");

                if (newPaginationText && paginationText) {
                    paginationText.innerHTML = newPaginationText.innerHTML;
                }

                if (newLoadMoreButton) {
                    this.currentPage = parseInt(
                        newLoadMoreButton.dataset.currentPage ||
                            targetPage.toString(),
                        10
                    );
                    this.totalPages = parseInt(
                        newLoadMoreButton.dataset.totalPages ||
                            this.totalPages.toString(),
                        10
                    );

                    loadMoreButton.dataset.currentPage =
                        this.currentPage.toString();
                    loadMoreButton.dataset.totalPages =
                        this.totalPages.toString();
                }

                this.updateButtonVisibility();
            }
        } catch (error) {
            console.error(
                `Error updating pagination from page ${targetPage}:`,
                error
            );
        }
    }

    resetPagination() {
        this.currentPage = 1;
        const loadMoreButton = this.querySelector(".load-more-button");
        if (loadMoreButton) {
            loadMoreButton.dataset.currentPage = "1";
        }
        this.updateButtonVisibility();
    }

    reinitButton() {
        const loadMoreButton = this.querySelector(".load-more-button");
        if (!loadMoreButton) return;

        const newButton = loadMoreButton.cloneNode(true);
        loadMoreButton.parentNode.replaceChild(newButton, loadMoreButton);

        newButton.addEventListener("click", (e) => {
            e.preventDefault();
            this.loadNextPage();
        });

        newButton.dataset.currentPage = this.currentPage.toString();
        newButton.dataset.totalPages = this.totalPages.toString();
    }
}

customElements.define("atlas-collection-load-more", AtlasCollectionLoadMore);

document.addEventListener("DOMContentLoaded", () => {
    if (typeof FacetFiltersForm !== "undefined") {
        const originalRenderProductGridContainer =
            FacetFiltersForm.renderProductGridContainer;

        FacetFiltersForm.renderProductGridContainer = function (html) {
            originalRenderProductGridContainer.call(this, html);

            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.has("page")) {
                urlParams.delete("page");
                const newUrl = `${window.location.pathname}${urlParams.toString() ? "?" + urlParams.toString() : ""}`;
                window.history.replaceState(
                    { searchParams: urlParams.toString() },
                    "",
                    newUrl
                );
            }

            setTimeout(() => {
                const loadMoreComponent = document.querySelector(
                    "atlas-collection-load-more"
                );
                const currentProductGrid =
                    document.getElementById("product-grid");

                const parser = new DOMParser();
                const doc = parser.parseFromString(html, "text/html");
                const newProductGrid = doc.getElementById("product-grid");
                const newPagination = doc.querySelector(
                    ".atlas-collection-pagination"
                );

                if (currentProductGrid && newProductGrid && loadMoreComponent) {
                    loadMoreComponent.updateProductGridAttributes(
                        currentProductGrid,
                        newProductGrid
                    );
                }

                let activeComponent = loadMoreComponent;
                if (
                    !activeComponent ||
                    !activeComponent.querySelector(".load-more-button")
                ) {
                    activeComponent = document.querySelector(
                        "atlas-collection-load-more"
                    );
                    if (activeComponent && !activeComponent.isInitialized) {
                        activeComponent.init();
                        activeComponent.isInitialized = true;
                    }
                }

                if (activeComponent) {
                    if (newPagination) {
                        const newLoadMoreButton =
                            newPagination.querySelector(".load-more-button");
                        if (newLoadMoreButton) {
                            activeComponent.currentPage = parseInt(
                                newLoadMoreButton.dataset.currentPage || "1",
                                10
                            );
                            activeComponent.totalPages = parseInt(
                                newLoadMoreButton.dataset.totalPages || "1",
                                10
                            );

                            const currentButton =
                                activeComponent.querySelector(
                                    ".load-more-button"
                                );
                            if (currentButton) {
                                currentButton.dataset.currentPage =
                                    activeComponent.currentPage.toString();
                                currentButton.dataset.totalPages =
                                    activeComponent.totalPages.toString();

                                const newPaginationText =
                                    newPagination.querySelector(
                                        ".pagination-text"
                                    );
                                const currentPaginationText =
                                    activeComponent.querySelector(
                                        ".pagination-text"
                                    );
                                if (
                                    newPaginationText &&
                                    currentPaginationText
                                ) {
                                    currentPaginationText.innerHTML =
                                        newPaginationText.innerHTML;
                                }
                            }

                            activeComponent.updateButtonVisibility();
                            activeComponent.reinitButton();
                        }
                    } else {
                        const currentButton =
                            activeComponent.querySelector(".load-more-button");
                        if (currentButton) {
                            currentButton.style.display = "none";
                        }
                        activeComponent.currentPage = 1;
                        activeComponent.totalPages = 1;
                    }

                    activeComponent.loadPagesFromURL();
                }
            }, 100);
        };
    }
});
